import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { rootUrl } from '../../helpers';
import { useAuth } from '../../context/auth-context';
import { toast } from 'react-toastify';
import { 
  FaUpload, 
  FaFileExcel, 
  FaCalendarAlt, 
  FaPaperPlane, 
  FaTable, 
  FaCheckCircle,
  FaSpinner
} from 'react-icons/fa';

export default function Input() {
  const [file, setFile] = useState(null);
  const [data, setData] = useState([]);
  const [type, setType] = useState('');
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [columns, setColumns] = useState([]);
  const [uploading, setUploading] = useState(false);

  const auth = useAuth();

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFile(file);
    }

     // Read the file
     const reader = new FileReader();
     reader.onload = (e) => {
       // Parse the file
       const binaryStr = e.target.result;
       const workbook = XLSX.read(binaryStr, { type: 'binary' });
 
       // Assuming the first sheet is used
       const sheetName = workbook.SheetNames[0];
       const worksheet = workbook.Sheets[sheetName];
       const jsonData = XLSX.utils.sheet_to_json(worksheet);
 
       // Extract headers for columns
       const headers = Object.keys(jsonData[0] || {}).map(header => ({
         name: header,
         selector: row => row[header],
         sortable: true
       }));
 
       setColumns(headers);
       setData(jsonData);
       
     };
     reader.readAsBinaryString(file);
  };

  const handleFileUpload = () => {
    if (!file) {
      toast.error('Please select a file first.');
      return;
    }
    if (!type) {
      toast.error('Please select a type.');
      return;
    }
    if (type === 'budget' && !year) {
      toast.error('Please select a year for budget upload.');
      return;
    }
    sendToBackend(data);
  };

  

  const sendToBackend = async (data) => {
    setUploading(true);
    const fd = {
        data: data,
        user: auth.user.id
    };

    // Add year for budget uploads
    if (type === 'budget') {
      fd.year = year;
    }

    try {
      const url = type === 'budget' ? `/importBudget` : `/importValuation`;
      const response = await axios.post(rootUrl(url), fd);
      if(response.data.status == 200){
        toast.success("Upload successful")
        setData([]);
        setFile(null);
        setType('');
        setColumns([]);
      }
    } catch (error) {
      console.error('Error sending data:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Error uploading file')
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-950 to-blue-800 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center gap-4">
          <div className="bg-white/20 rounded-xl p-3">
            <FaUpload className="text-2xl" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">Data Upload</h1>
            <p className="text-blue-100 text-sm mt-1">Upload Excel files for budget or valuation roll</p>
          </div>
        </div>
      </div>

      {/* Upload Form */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="space-y-6">
          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <div className="flex items-center gap-2">
                <FaFileExcel className="text-gray-500" />
                <span>Select Excel File</span>
              </div>
            </label>
            <div className="relative">
              <input
                type="file"
                accept=".xlsx, .xls"
                onChange={handleFileChange}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-950 hover:file:bg-blue-100 file:cursor-pointer cursor-pointer"
              />
              {file && (
                <div className="mt-2 flex items-center gap-2 text-sm text-green-600">
                  <FaCheckCircle />
                  <span className="font-medium">{file.name}</span>
                </div>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">Accepted formats: .xlsx, .xls</p>
          </div>

          {/* Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <div className="flex items-center gap-2">
                <FaTable className="text-gray-500" />
                <span>Upload Type</span>
              </div>
            </label>
            <select 
              name="type" 
              id="type" 
              className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              <option value="">Select Type</option>
              <option value="budget">Budget</option>
              <option value="valuation">Valuation Roll</option>
            </select>
          </div>

          {/* Year Input (for Budget) */}
          {type === 'budget' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center gap-2">
                  <FaCalendarAlt className="text-gray-500" />
                  <span>Year</span>
                </div>
              </label>
              <input
                type="number"
                placeholder="Enter year"
                className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                value={year}
                onChange={(e) => setYear(e.target.value)}
                min="2000"
                max="2099"
              />
            </div>
          )}

          {/* Upload Button */}
          <div className="flex justify-end pt-4 border-t border-gray-200">
            <button 
              className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-blue-950 to-blue-800 text-white rounded-lg hover:shadow-lg transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleFileUpload}
              disabled={uploading || !file || !type || (type === 'budget' && !year)}
            >
              {uploading ? (
                <>
                  <FaSpinner className="animate-spin" />
                  <span>Uploading...</span>
                </>
              ) : (
                <>
                  <FaPaperPlane />
                  <span>Upload File</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Data Preview */}
      {data.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 rounded-lg p-2">
                <FaTable className="text-blue-600 text-xl" />
              </div>
              <div>
                <h3 className="font-bold text-lg text-gray-900">Data Preview</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  {data.length} {data.length === 1 ? 'row' : 'rows'} loaded
                </p>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gradient-to-r from-blue-950 to-blue-800 text-white">
                  {columns.map((col, index) => (
                    <th key={index} className="px-6 py-3 text-left font-semibold text-sm border-r border-blue-700 last:border-r-0">
                      {col.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.slice(0, 10).map((row, rowIndex) => (
                  <tr 
                    key={rowIndex} 
                    className="border-b border-gray-200 hover:bg-gray-50 transition-colors"
                  >
                    {columns.map((col, colIndex) => (
                      <td key={colIndex} className="px-6 py-3 text-sm text-gray-700 border-r border-gray-200 last:border-r-0">
                        {row[col.name] || '-'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {data.length > 10 && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 text-center text-sm text-gray-600">
                Showing first 10 of {data.length} rows. All rows will be uploaded.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
